import { useEffect, useState } from "react";
import {
    useDisclosureStore,
    useCategoryStore,
    useCacheStore,
    useTopicStore,
    useHelperStore,
    useTabStore,
    useCompanyStore
} from "~/stores";

import useSolrMetaData from "./useSolrMetaData";
import { ISetCacheListParams } from "~/stores/cache/cache.state";
import { SEARCH_TABS } from "~/types/search.types";
import { SearchActions } from "~/types/actions.enum";
import useCompanyList from "./useCompanyList";
import useFetchFilterData from "~/hooks/useFetchFilterData";

/**
 * Initializes (fetches) the Graphics and Reports type - Categories and Topics count
 * and keeps them in the `useCacheStore()` store.
 */
const useInit = () => {
    const [type, setType] = useState("");
    const { fetchMetadata } = useSolrMetaData();
    const { fetchCompanies } = useCompanyList();
    const { fetchCompaniesData, fetchIndustriesData } = useFetchFilterData();
    const REGION = import.meta.env.VITE_REGION;

    // Store dispatch methods
    const dispatchCache = useCacheStore(state => state.dispatch);
    const dispatchCategories = useCategoryStore(state => state.dispatch);
    const dispatchTopics = useTopicStore(state => state.dispatch);
    const dispatchHelper = useHelperStore(state => state.dispatch);
    const dispatchCompanies = useCompanyStore(state => state.dispatch);

    // Store states
    const disclosures = useDisclosureStore(state => state.disclosures);
    const categories = useCategoryStore(state => state.categories);
    const topics = useTopicStore(state => state.topics);
    const activeTab = useTabStore(state => state.tab);
    const isAppInitialized = useHelperStore(state => state.isAppInitialized);

    // Caches
    const graphicsCategories = useCacheStore(state => state.graphicsCategories);
    const grapchicsTopics = useCacheStore(state => state.graphicsTopics);
    const reportsCategories = useCacheStore(state => state.reportsCategories);
    const reportsTopics = useCacheStore(state => state.reportsTopics);

    // Fetch all metatada and topics documents count only once
    const fetchDocumentsData = async () => {
        setType(SEARCH_TABS.REPORTS);
        await fetchMetadata(SEARCH_TABS.REPORTS);

        setType(SEARCH_TABS.IMAGES);
        await fetchMetadata(SEARCH_TABS.IMAGES);

        // Load company selection for FR version
        if (REGION === "fr") {
            await fetchCompanies();
        }

        dispatchHelper({ type: SearchActions.SET_IS_INITIALIZED, payload: true });
    };

    useEffect(() => {
        if (disclosures.length === 0 || isAppInitialized) {
            return;
        }

        fetchCompaniesData();
        fetchIndustriesData();
        fetchDocumentsData();
    }, [disclosures]);

    useEffect(() => {
        if (categories.length === 0 || categories[0].count === -1 || isAppInitialized) {
            return;
        }

        // Set the Categories "pages" or "images" type cache
        const params = { type, data: categories } as ISetCacheListParams;
        dispatchCache({ type: SearchActions.SET_CACHE_CATEGORIES, payload: params });

        // Reset the active Category list to enable running this effect for the "images" type
        if (type === SEARCH_TABS.REPORTS) {
            dispatchCategories({ type: SearchActions.SET_CATEGORY_ITEMS, payload: [] });
        }
    }, [categories]);

    useEffect(() => {
        if (topics.length === 0 || topics[0].count === -1 || isAppInitialized) {
            return;
        }

        // Set the Topics "pages" or "images" type cache
        const params = { type, data: topics } as ISetCacheListParams;
        dispatchCache({ type: SearchActions.SET_CACHE_TOPICS, payload: params });

        // Reset the active Topics list to enable running this effect for the "images" type
        if (type === SEARCH_TABS.REPORTS) {
            dispatchTopics({ type: SearchActions.SET_TOPIC_ITEMS, payload: [] });
        }
    }, [topics]);

    useEffect(() => {
        if (!isAppInitialized) {
            return;
        }

        // Switch data from cache according to the current selected tab
        if (activeTab?.key === SEARCH_TABS.IMAGES) {
            dispatchCategories({
                type: SearchActions.SET_CATEGORY_ITEMS,
                payload: graphicsCategories
            });
            dispatchTopics({ type: SearchActions.SET_TOPIC_ITEMS, payload: grapchicsTopics });
        }

        if (activeTab?.key === SEARCH_TABS.REPORTS) {
            dispatchCategories({
                type: SearchActions.SET_CATEGORY_ITEMS,
                payload: reportsCategories
            });
            dispatchTopics({ type: SearchActions.SET_TOPIC_ITEMS, payload: reportsTopics });
        }

        //Reset full documents from one page/tab to another
        if (activeTab?.key === SEARCH_TABS.FULL_DOCUMENTS) {
            dispatchCompanies({ type: SearchActions.RESET_SELECTION });
        }
    }, [activeTab]);
};

export default useInit;
